import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import {
  captureStationSetting,
  captureStationSettings,
  resolveStationSetting,
  subscribeToStationSettings,
  type StationSetting,
} from '../../utils/station/settings';

/**
 * Reads a station setting and keeps the component in step with it, whether it
 * changes through the URL or through a settings panel.
 */
export const useStationSetting = <T>(setting: StationSetting<T>): T => {
  const location = useLocation();

  const value = useSyncExternalStore(
    subscribeToStationSettings,
    () => resolveStationSetting(setting, location.search),
    () => resolveStationSetting(setting, ''),
  );

  useEffect(() => {
    captureStationSetting(setting, location.search);
  }, [setting, location.search]);

  return value;
};

/**
 * Remembers every station parameter the kiosk arrives with, on whatever page it
 * lands on. Without this, a parameter is lost as soon as the app redirects.
 */
export const useStationSettingsCapture = (): void => {
  const location = useLocation();

  useEffect(() => {
    captureStationSettings(location.search);
  }, [location.search]);
};
